import React from 'react';
import './SetView.css';
import StatsList from '../StatsList';
import * as st from '../../core/st';
import ItemCard from './ItemCard';
import { useEquipItem } from '../../hooks/useEquipItem';

const TXT_ITEMS = { fr: 'Items', en: 'Items', de: 'Gegenstände', es: 'Objetos', pt: 'Itens' };

const getTierEffects = (tier, fallbackNumItems) => {
    let effects = [];
    let numItems = fallbackNumItems;
    // Ceilings the tier puts on a characteristic rather than points it grants;
    // they travel beside the effects, never inside. See backend core/set_caps.py.
    let caps = [];

    if (tier && typeof tier === 'object') {
        if (tier.effects && Array.isArray(tier.effects)) {
            effects = tier.effects;
            numItems = tier.num_items_required || numItems;
            if (Array.isArray(tier.caps)) caps = tier.caps;
        } else if (Array.isArray(tier)) {
            effects = tier;
        }
    }

    return { effects, numItems, caps };
};

/** `{id, icon, value, isCap?}` rows StatsList renders, dropping unknown stats. */
const toStatRows = (lines, isCap) => (lines || [])
    .map(line => {
        const stat = st.STATS.find(s => String(s.id) === String(line?.characteristic));
        if (!stat) return null;
        const value = isCap ? Number(line.value) : getBonusValue(line);
        return { id: stat.id, icon: stat.icon, value, isCap };
    })
    .filter(Boolean);

const getBonusValue = (bonusEffect) => {
    if (!bonusEffect) return 0;
    if (bonusEffect.value !== undefined) return bonusEffect.value;
    if (bonusEffect.min !== undefined) return bonusEffect.min;
    if (bonusEffect.max !== undefined) return bonusEffect.max;
    return 0;
};

const SetView = ({ set, lang = 'fr', onItemClick = null }) => {
    const { equipItem } = useEquipItem(lang);
    const handleItemClick = onItemClick || ((item) => equipItem(item, null));

    if (!set) return null;

    const allItems = set.items || [];
    const bonusTiers = set.bonuses ? [...set.bonuses].reverse() : [];

    return (
        <div className="set-view">
            <h5 className="set-view-name item-card-name">{set.name[lang]}</h5>

            {bonusTiers.length > 0 && (
                <div className="set-view-bonuses">
                    {bonusTiers.map((tier, index) => {
                        const originalIndex = bonusTiers.length - 1 - index;
                        const fallbackNumItems = originalIndex + 2;
                        const { effects, numItems, caps } = getTierEffects(tier, fallbackNumItems);
                        // The set-detail payload keeps its ceilings in a parallel
                        // list instead of inside the tier.
                        const tierCaps = caps.length
                            ? caps
                            : (set.bonus_caps?.[originalIndex] || []);

                        if ((!effects || effects.length === 0) && tierCaps.length === 0) return null;

                        const tierStats = [
                            ...toStatRows(effects, false),
                            ...toStatRows(tierCaps, true),
                        ];

                        return (
                            <div key={index} className="set-view-bonus-tier">
                                <h6 className="set-view-bonus-tier-label">{numItems} {TXT_ITEMS[lang] || TXT_ITEMS.en}</h6>
                                <StatsList
                                    stats={tierStats}
                                    className="set-view-bonuses-list"
                                    lang={lang}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="set-view-items-list">
                {allItems.map((item, index) => (
                    <ItemCard
                        key={`item-${index}`}
                        item={item}
                        lang={lang}
                        compact={true}
                        showType={true}
                        onItemClick={handleItemClick}
                    />
                ))}
            </div>
        </div>
    );
};

export default SetView;
